import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Input } from '@rallyshift/design-system'
import api from '../api/client'
import { useLocale } from '../contexts/LocaleContext'
import { useOrganization } from '../contexts/OrganizationContext'
import AuthShell from '../components/AuthShell'

/**
 * The per-organisation public signup page. Which organisation someone lands on comes
 * from the host, so the same code serves every tenant. Organisations that only want
 * invited volunteers turn this off in their settings.
 */
const VolunteerSignupPage: React.FC = () => {
  const { t } = useLocale()
  const navigate = useNavigate()
  const { organization } = useOrganization()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showRecovery, setShowRecovery] = useState(false)

  const inviteOnly = organization ? !organization.allow_public_signup : false

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setShowRecovery(false)

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'))
      return
    }
    if (password.length < 6) {
      setError(t('auth.passwordTooShort'))
      return
    }

    setLoading(true)
    try {
      await api.auth.register({ email, password, firstName, lastName })
      setSuccess(t('auth.signupSuccess'))
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      setFirstName('')
      setLastName('')
    } catch (err: any) {
      const conflict =
        err.status === 409 ||
        String(err.body?.message ?? '')
          .toLowerCase()
          .includes('already exists')
      setError(conflict ? t('auth.accountExists') : err.body?.message || err.message || t('auth.signupError'))
      setShowRecovery(conflict)
    } finally {
      setLoading(false)
    }
  }

  const recoverPassword = async () => {
    if (!email) {
      setError(t('auth.enterEmailFirst'))
      return
    }
    setLoading(true)
    try {
      await api.auth.forgotPassword(email)
      setError('')
      setShowRecovery(false)
      setSuccess('We hebben je een link gestuurd om je wachtwoord opnieuw in te stellen.')
    } catch (err: any) {
      setError(err.message || t('auth.resetEmailFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (inviteOnly) {
    return (
      <AuthShell
        title="Alleen op uitnodiging"
        subtitle={`${organization?.name} stelt zelf haar vrijwilligers samen.`}
        footer={
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="font-semibold text-action-primary underline-offset-2 hover:underline"
          >
            Ik heb al een account
          </button>
        }
      >
        <p className="text-md text-text-secondary">
          Vraag de organisator om een uitnodiging. Je krijgt dan een link waarmee je in
          één stap een account maakt en meteen meedoet.
        </p>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={t('auth.signUp')}
      subtitle="Maak je account aan. Na het bevestigen van je e-mail vul je je profiel aan en meld je je aan als vrijwilliger."
      footer={
        <>
          <span className="text-text-secondary">Heb je al een account? </span>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="font-semibold text-action-primary underline-offset-2 hover:underline"
          >
            Inloggen
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4">
          <Alert variant="danger" onDismiss={() => setError('')}>
            {error}
            {showRecovery && (
              <button
                type="button"
                onClick={recoverPassword}
                className="mt-2 block font-semibold underline underline-offset-2"
              >
                Stel in plaats daarvan je wachtwoord opnieuw in
              </button>
            )}
          </Alert>
        </div>
      )}
      {success && (
        <div className="mb-4">
          <Alert variant="success" onDismiss={() => setSuccess('')}>
            {success}
          </Alert>
        </div>
      )}

      <form onSubmit={handleSignup} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={t('common.firstName')}
            size="lg"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label={t('common.lastName')}
            size="lg"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <Input
          label={t('auth.email')}
          type="email"
          size="lg"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label={t('auth.password')}
          type="password"
          size="lg"
          autoComplete="new-password"
          hint="Minstens 6 tekens."
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          label={t('auth.confirmPassword')}
          type="password"
          size="lg"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />

        <Button type="submit" variant="tenant" size="lg" fullWidth loading={loading}>
          {t('auth.signUp')}
        </Button>
      </form>
    </AuthShell>
  )
}

export default VolunteerSignupPage
